import React, { useEffect, useState } from 'react';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import MultiItemUpDown from '../../../shared/components/MultiItemUpDown';
import MuiInputDecimal from '../../../shared/components/MuiInputDecimal';
import MuiRadioGroup from '../../../shared/components/MuiRadioGroup';
import SkeletonWrapper from '../../../shared/components/skeleton/SkeletonWrapper';
import cx from 'classnames';

export const PERFORMANCE_METRIC_INPUT_TYPES = {
  text: 1,
  decimal: 2,
  multiselect: 3,
  radio: 4,
  plainText: 5
};

/**
 * @typedef {object} PerformanceMetricInputCardOptions
 * @property {number} key
 * @property {string|number} value
 */

/**
 * @typedef {object} PerformanceMetricInputCardProps
 * @property {string} title
 * @property {React.ReactElement} [titleAsterisk]
 * @property {string} field
 * @property {number} type
 * @property {(fieldChange: { [field: string]: string | number }) => void} [onChange]
 * @property {number} [maxLength]
 * @property {PerformanceMetricInputCardOptions[]} [options]
 * @property {*} [errors]
 * @property {*} [value]
 * @property {string} [placeholder]
 * @property {boolean} [disabled]
 * @property {boolean} [viewOnlyMode]
 * @property {(field: string) => void} [handleOnClickMetricField]
 * @property {boolean} [loading]
 */

/**
 * @param {PerformanceMetricInputCardProps} props
 */
const PerformanceMetricInputCard = props => {
  const {
    title,
    maxLength,
    type,
    field,
    options,
    onChange,
    placeholder,
    handleOnClickMetricField,
    titleAsterisk = null,
    errors = {},
    value = '',
    disabled = false,
    viewOnlyMode = false,
    loading = false
  } = props;

  const [inputValue, setInputValue] = useState('');
  const isMultiline = maxLength && maxLength > 100;
  const placeholderText = placeholder || `Enter ${title}`;
  const fieldError = errors[field];
  const hasError = fieldError && fieldError.hasError;

  useEffect(() => {
    if (!value) {
      return;
    }
    setInputValue(value);
  }, [value]);

  const handleOnChange = e => {
    if (type === PERFORMANCE_METRIC_INPUT_TYPES.multiselect) {
      setInputValue(e);
      onChange({ [`${field}`]: e });
      return;
    }

    const { value } = e.target;
    setInputValue(value);
    onChange({ [`${field}`]: value });
  };

  const handleOnChangeRadioGroup = e => {
    const value = Number(e.target.value);
    setInputValue(value);
    onChange({ [`${field}`]: value });
  };

  const handleClickAndFocusField = e => {
    e.stopPropagation();
    handleOnClickMetricField(field);
  };

  const getSelectedOption = value => {
    const index = options.findIndex(item => item.key === value);
    return options[index].value;
  };

  const renderSelectedOption = value =>
    value ? getSelectedOption(value) : <span className='placeholder'>{placeholderText}</span>;

  return (
    <div
      className='input-box'
      onFocus={handleClickAndFocusField}
      onClick={handleClickAndFocusField}>
      <div data-testid={`input-container-${field}`} className={`input-container input-${field}`}>
        <p className='input-box__title'>
          {title}
          {titleAsterisk}
        </p>
        {type === PERFORMANCE_METRIC_INPUT_TYPES.text && (
          <SkeletonWrapper show={loading}>
            <MuiInputTextWithRemainingChars
              data-testid='text-input'
              className={cx('input-box__text-input', { multiline: isMultiline, error: hasError })}
              disableUnderline
              placeholder={placeholderText}
              onChange={handleOnChange}
              multiline={isMultiline}
              rows={isMultiline ? 5 : 1}
              value={inputValue}
              disabled={viewOnlyMode || disabled}
              maxLength={maxLength}
              hideRemainingChars={viewOnlyMode}
            />
          </SkeletonWrapper>
        )}
        {type === PERFORMANCE_METRIC_INPUT_TYPES.decimal && (
          <SkeletonWrapper show={loading}>
            <MuiInputDecimal
              className={cx('input-box__text-input', { error: hasError })}
              placeholder={placeholderText}
              value={value}
              handleOnChange={handleOnChange}
              disabled={viewOnlyMode || disabled}
            />
          </SkeletonWrapper>
        )}
        {type === PERFORMANCE_METRIC_INPUT_TYPES.multiselect && (
          <div className={cx('input-box__multiselect-container', { error: hasError })}>
            <MultiItemUpDown
              className='input-box__multiselect'
              options={options}
              value={inputValue}
              setValue={handleOnChange}
              displayEmpty
              renderValue={renderSelectedOption}
              disabled={viewOnlyMode || disabled}
              preventUpDownBtnTabFocus={true}
              loading={loading}
            />
          </div>
        )}
        {type === PERFORMANCE_METRIC_INPUT_TYPES.radio && (
          <div className={cx('input-box__radio-container', { error: hasError })}>
            <MuiRadioGroup
              handleOnChange={handleOnChangeRadioGroup}
              options={options}
              disabled={viewOnlyMode || disabled}
              input={Number(inputValue)}
              loading={loading}
            />
          </div>
        )}
        {type === PERFORMANCE_METRIC_INPUT_TYPES.plainText && (
          <SkeletonWrapper show={loading} inheritParentSize={true}>
            <div className='input-box__plain-text-container'>
              <span>{value}</span>
            </div>
          </SkeletonWrapper>
        )}
      </div>
    </div>
  );
};

export default PerformanceMetricInputCard;
